import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  PAGE_SEO_UPDATED_EVENT,
  getCachedPageSEO,
  hasCachedPageSEO,
  loadPageSEO,
  normalizePagePath,
  type PageSEO,
  clearPageSEOCache as clearCache,
} from '@/lib/pageSeoSettings';

interface DynamicSEOHeadProps {
  pagePath: string;
  fallbackTitle: string;
  fallbackDescription: string;
  fallbackKeywords?: string;
  image?: string;
  url?: string;
}

export const DynamicSEOHead = ({
  pagePath,
  fallbackTitle,
  fallbackDescription,
  fallbackKeywords = '',
  image = '/placeholder.svg',
  url = window.location.href,
}: DynamicSEOHeadProps) => {
  const normalizedPath = useMemo(() => normalizePagePath(pagePath), [pagePath]);

  const [seo, setSeo] = useState<PageSEO | null>(() => {
    const cached = getCachedPageSEO(normalizedPath);
    return cached ?? null;
  });

  useEffect(() => {
    let cancelled = false;

    const ensureLoaded = async () => {
      if (!hasCachedPageSEO(normalizedPath)) {
        const fetched = await loadPageSEO(normalizedPath);
        if (!cancelled) setSeo(fetched);
        return;
      }

      // If cache exists (even if null), sync state from it
      if (!cancelled) setSeo(getCachedPageSEO(normalizedPath) ?? null);
    };

    ensureLoaded();
    return () => {
      cancelled = true;
    };
  }, [normalizedPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onSEOUpdated = (evt: Event) => {
      const detail = (evt as CustomEvent<{ pagePath?: string }>).detail;
      const updatedPath = detail?.pagePath ? normalizePagePath(detail.pagePath) : undefined;

      if (!updatedPath || updatedPath === normalizedPath) {
        loadPageSEO(normalizedPath).then((fetched) => setSeo(fetched));
      }
    };

    window.addEventListener(PAGE_SEO_UPDATED_EVENT, onSEOUpdated as EventListener);
    return () => window.removeEventListener(PAGE_SEO_UPDATED_EVENT, onSEOUpdated as EventListener);
  }, [normalizedPath]);

  const title = seo?.title || fallbackTitle;
  const description = seo?.description || fallbackDescription;
  const keywords = seo?.keywords || fallbackKeywords;
  const ogImage = seo?.og_image || image;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

// Backwards-compatible re-export for existing imports
export function clearPageSEOCache(pagePath?: string) {
  clearCache(pagePath);
}
